/* eslint-disable max-len */
import React from 'react';
import Scrollbars from 'react-custom-scrollbars';
import styled from 'styled-components';
import { LFC } from '../../../Legacy';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { UserLiteData } from '../../../api/ApiTypes';
import { useLoader, useSubmitter } from '../../../platform/Hook';
import { LoadingGuard } from '../../../ui/LoadingGuard';
import { ResultInfoForSubmitter } from '../../../ui/ResultInfo';
import { useResolve } from '../../Container';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { BoardInfo } from '../BoardInfo';
import { EditorSession } from '../EditorSession';
import { Button, Colors } from './../../../ui';
import { ParticipantCounter } from './Counter';

const XScrollbars = Scrollbars as any;

const Stack = styled.div` 
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
`;

const StackHeading = styled.div`
    display: flex;
    align-items: center;
    padding: .5rem 1.5rem;
    margin-top: .75rem;
    color: ${Colors.text};

    &:first-child {
        margin: 0;
    }
`;

const StackActions = styled.div`
    flex: 1 1 auto; 
    display: flex;
    align-items: center;
    justify-content: center;
    visibility: hidden;
`;

const StackItem = styled.div`
    display: flex;
    padding: .75rem 1.5rem;
    border-top: solid 1px #e9ebee;

    &:last-child {
        border-bottom: solid 1px #e9ebee;
    }

    &:hover {
        background: #F0F2F5;
        ${StackActions} {
            visibility: visible;
        }
    }
`;

const StackBlock = styled.div<{ push?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${p => p.push ? 'margin-left: auto' : ''};
`;

const StackTitle = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    font-weight: 450;
`;

const StackDetail = styled.div`
    display: flex;
    align-items: center;
`;

const StackDetailInfo = styled.div`
    display: flex;
    align-items: center;
    height: 1.5rem;
    font-size: .875rem;
    color: #8390a7;
`;

const GuestBadge = styled.div`
    display: inline-block;
    position: relative;
    margin-left: .5em;
    padding: .1rem .2rem;
    border-radius: .15rem;
    color: white;
    font-weight: 450;
    text-transform: uppercase;
    font-size: .75rem;
    background: ${Colors.positive};
`;

const UserStackItem: LFC<{ user: UserLiteData }> = ({ user, children }) => {
    return <StackItem>
        <StackBlock>
            <ParticipantCounter info={user} />
            <Flexer flex padding="0 0 0 .75">
                <StackTitle>
                    {user.name}
                    {!!user.external && <GuestBadge>Guest</GuestBadge>}
                </StackTitle>
                <StackDetail>
                    <StackDetailInfo>{user.email.endsWith('guest.metroretro.io') ? '(no email)' : user.email}</StackDetailInfo>
                </StackDetail>
            </Flexer>
        </StackBlock>
        <StackBlock push>
            <StackActions>
                {children}
            </StackActions>
        </StackBlock>
    </StackItem>;
};

export const ManageAccessView = React.memo(() => {

    const api = useResolve(ApiClient);
    const board = useResolve(EditorSession).get(BoardInfo);

    const [l1, viaBoard, _, { update }] = useLoader(() => api.exec(v2.boards.members.list(board.id)), [board]);
    const [l2, viaWorkspace] = useLoader(() => api.exec(v2.workspaces.members.list(board.workspaceId)), [board]);
    const loading = l1 || l2;

    const [onRemove, working, result] = useSubmitter(async (userId: string) => {
        await api.exec(v2.boards.members.remove(board.id, userId));
        update(items => items.filter(x => x.id !== userId));
    }, [board, update]);

    return <Flexer flex minWidth={48} height={28}>
        <LoadingGuard loading={loading}>
            <XScrollbars style={{ flex: '1 1 auto' }}>
                <ResultInfoForSubmitter result={result} />
                <Stack>
                    {!!viaBoard?.length && <>
                        <StackHeading>
                            <StackBlock>
                                Guests / Non-Team Members ({viaBoard.length})
                            </StackBlock>
                        </StackHeading>
                        {viaBoard.map(x => <UserStackItem key={x.id} user={x}>
                            <Button disabled={working} onAction={() => onRemove(x.id)}>Remove</Button>
                        </UserStackItem>)}
                    </>}
                    {!!viaWorkspace?.length && <>
                        <StackHeading>
                            <StackBlock>
                                Team Members ({viaWorkspace.length})
                            </StackBlock>
                        </StackHeading>
                        {viaWorkspace.map(x => <UserStackItem key={x.id} user={x} />)}
                    </>}
                </Stack>
            </XScrollbars>
        </LoadingGuard>
    </Flexer>;
});
